import {fetchWrapper} from "@/helpers/fetch-wrapper";
import {useUserActivityFiltersStore} from "@/stores/userActivityFilters";

/**
 * Serwis do operacji na użytkownikach, aktywnościach i uprawnieniach.
 */
class Users {
    stripFilterParam(queryString, key) {
        if (!queryString) {
            return '';
        }
        const params = new URLSearchParams(queryString.startsWith('&') ? queryString.slice(1) : queryString);
        params.delete(key);
        const serialized = params.toString();
        return serialized ? `&${serialized}` : '';
    }

    /**
     * Tworzy query string na podstawie filtrów (np. wyszukiwarka użytkowników, aktywności).
     * Pobiera filtry z store, buduje parametry GET.
     */
    getQueryString() {
        const filtersStore = useUserActivityFiltersStore();
        const keys = filtersStore && filtersStore.filters ? Object.keys(filtersStore.filters) : [];

        // Generuje query string na podstawie kluczy i wartości filtrów (pomija puste)
        return keys.length > 0 
          ? '&' + keys
            .map((key) => (filtersStore.filters[key] && (filtersStore.filters[key].length > 0 || filtersStore.filters[key] > 0)) 
                ? key + "=" + filtersStore.filters[key] : '')
            .join("&") 
          : '';
    }
    
    /**
     * Zwraca wybrany typ zdarzeń (eventTypes) z filtra aktywności użytkownika.
     */
    getEventTypes() {
        const filtersStore = useUserActivityFiltersStore();
        return filtersStore?.filters?.eventTypes ?? null;
    }

    /**
     * Pobiera listę użytkowników z API (wszystkich, sortowanych rosnąco).
     */
    getUserList() {
        return fetchWrapper.getWithCount(`${process.env.VUE_APP_API_URL}/users?orderDirection=asc`);
    }

    /**
     * Pobiera listę aktywności użytkowników lub logów VMS, zależnie od typu zdarzenia.
     * - eventType == null lub <3: normalne logowania do stacji,
     * - eventType == 3: logi VMS (inne API, inny endpoint).
     */
    getUserActivityList(pageNum, pageSize) {
        const eventTypeRaw = this.getEventTypes();
        const eventType = eventTypeRaw != null && eventTypeRaw !== '' ? Number(eventTypeRaw) : null;
        let queryString = this.getQueryString();

        if (eventType === null || Number.isNaN(eventType)) {
            // Brak wybranego eventType pobiera logowania
            return fetchWrapper.getWithCount(
                `${process.env.VUE_APP_API_URL}/login-to-locations?orderDirection=desc&page=${pageNum}&limit=${pageSize}` + queryString
            );
        }
        if (eventType === 3) {
            // Dla VMS endpoint oczekuje innych eventTypes (enable/disable), więc usuwamy filtr eventTypes z query.
            queryString = this.stripFilterParam(queryString, 'eventTypes');
            return fetchWrapper.getWithCount(
                `${process.env.VUE_APP_API_URL}/vms-logs?orderDirection=desc` + queryString
            );
        }

        // Pozostałe typy (1,2,4,5,6,7,8,9) obsługuje rozszerzony endpoint login-to-locations.
        return fetchWrapper.getWithCount(
            `${process.env.VUE_APP_API_URL}/login-to-locations?orderDirection=desc&page=${pageNum}&limit=${pageSize}` + queryString
        );
    }

    /**
     * Dodaje nowego użytkownika (POST).
     * @param {Object} objectData - dane użytkownika
     */
    addUser(objectData) {
        console.log('objectdata: ', objectData);
        return fetchWrapper.post(`${process.env.VUE_APP_API_URL}/users`, objectData);
    }

    /**
     * Pobiera dane użytkownika po ID.
     */
    fetchUser(id) {
        return fetchWrapper.get(`${process.env.VUE_APP_API_URL}/users/${id}`);
    }

    /**
     * Aktualizuje użytkownika po ID (PUT).
     * @param {Number} id - ID użytkownika
     * @param {Object} userData - nowe dane
     */
    updateUser(id, userData) {
        return fetchWrapper.put(`${process.env.VUE_APP_API_URL}/users/${id}`, userData);
    }

    /**
     * Usuwa użytkowników (bulk delete przez POST).
     * @param {Array} ids - tablica ID do usunięcia
     */
    deleteUsers(ids) {
        return fetchWrapper.post(`${process.env.VUE_APP_API_URL}/users/bulk-delete`, ids);
    }

    /**
     * Usuwa sesję użytkownika z bazy (wymuszone wylogowanie).
     * @param {Number} userId - ID użytkownika
     */
    killUserSession(userId) {
        return fetchWrapper.delete(`${process.env.VUE_APP_API_URL}/users/${userId}/session`);
    }

    /**
     * Pobiera listę ról dostępnych w systemie (do formularzy, dropdownów, uprawnień).
     */
    getRolesList() {
        return fetchWrapper.get(`${process.env.VUE_APP_API_URL}/roles`);
    }

    /**
     * Pobiera raport logowań użytkowników w zadanym zakresie dat (ZIP, CSV).
     */
    getZip(dateFrom, dateTo) {
        return fetchWrapper.get(
            `${process.env.VUE_APP_API_URL}/login-to-locations/report` +
            `?dateTimeStart=${dateFrom}&dateTimeEnd=${dateTo}` + this.getQueryString()
        );
    }

    /**
     * Pobiera plik (np. ZIP, CSV, PDF) jako blob (do pobrania).
     */
    fetch(zip) {
        let zipNoStart = zip.includes('api/v1') ? zip.substr(7) : zip;
        return fetchWrapper.getBlob(process.env.VUE_APP_API_URL + zipNoStart);
    }
}

// Eksport singletonu serwisu
export default new Users();
